import { roofDeckingVerticalThicknessMm } from '../../domain/roof/roofDecking';
import { roofFramePositions } from '../../domain/roof/roofFramePositions';
import {
	memberPiecesAround,
	type MemberRowTrim,
} from "../../domain/chimney/chimneyTrimmer";
import { planRoofJoistTrim, TRIMMED_PIECE_SUFFIX } from "./chimneyTrim";
import * as THREE from "three";
import { normalizeRoofSystemId } from "../../domain/roof/roofSystems";
import { ENGINE_CC_M as CC } from "../constants";
import {
	addBox,
	addSlopedBox,
} from "../geometry";
import {
	matBand,
	matExteriorCladding,
	matFascia,
	matFasciaHorizontal,
	matRoofFelt,
	matRoofFlashing,
	matRoofSheetMetal,
	matRoofTop,
	matRoofUnderlay,
	matWood,
} from "../materials";
import type { RoofBuildOptions, SquareBaseBuildContext } from "./context";

export const ROOF_UNDERLAY_T = 0.004;
const ROOF_FELT_T = 0.01;
const ROOF_SHEET_T = 0.012;
const ROOF_FLASHING_T = 0.004;

/**
 * A ridge detail is two roof-mounted wings, never one horizontal slab.
 * `ridgeContactY` is the underside of both wings at x=0; their centres are
 * lowered by their half-run rise so the full underside follows each roof face.
 */
function addBentGableRidgeStrip(options: {
	totalWidth: number;
	height: number;
	length: number;
	material: THREE.Material;
	ridgeContactY: number;
	pitchRad: number;
	group: THREE.Group;
	name: string;
}) {
	const assembly = new THREE.Group();
	assembly.name = options.name;
	options.group.add(assembly);
	const wingWidth = options.totalWidth / 2;
	const centerOffset = wingWidth / 2;
	const centerY =
		options.ridgeContactY +
		options.height / 2 -
		centerOffset * Math.tan(options.pitchRad);

	for (const side of ["left", "right"] as const) {
		const sign = side === "left" ? -1 : 1;
		const wing = addSlopedBox(
			wingWidth,
			options.height,
			options.length,
			options.material,
			sign * centerOffset,
			centerY,
			0,
			assembly,
			side === "left" ? options.pitchRad : -options.pitchRad,
			"x",
		);
		wing.name = `${options.name}-${side}`;
	}

	return assembly;
}

/**
 * Y of the roof joists' UNDERSIDE at a point — the plane the inner ceiling
 * hangs from, and the bottom of the bays its insulation fills.
 *
 * This lives here because `roof.ts` is what actually places the joists. The
 * ceiling used to derive its own plane by anchoring the fall at the FINISHED
 * ceiling edge instead of the wall line, which put the whole stack
 * `(sD + iL + pT)·tan(pitch)` below the bays it was supposed to fill — ~34 mm
 * on a default 22° gable, with the batts hanging under the joists and an
 * equal void left at their tops. Anchor to this, never re-derive it.
 */
export function getRoofJoistUndersideYAt(
	ctx: SquareBaseBuildContext,
	x = 0,
	z = 0,
): number {
	const tanPitch = Math.tan(ctx.pitchRad);

	if (ctx.roofConfig.kind === "gable") {
		// Joists bear on the top plate at the wall line and rise to the ridge.
		return (
			ctx.wallBase + ctx.H + (ctx.W / 2 - Math.abs(x)) * tanPitch
		);
	}

	const slope = ctx.roofGeometry.monoSlope;
	if (slope && slope.highWall !== "front") {
		// Any other high wall: the joist underside IS the roof line, which
		// starts at the low wall line (full wall height) and rises over the
		// slope run — unclamped, so the overhang extrapolates.
		return ctx.wallBase + ctx.roofGeometry.getRoofHeightAt(x, z);
	}

	// Mono/flat: level joists stepped up along z from the low (back) wall.
	return ctx.roofBaseBack + (z + ctx.L / 2) * tanPitch;
}

/**
 * Z centres of the roof joists (flat/mono) or joist pairs (gable). Joists
 * always SPAN the width (x) and are spread along the length (z), whatever
 * the roof kind — which is why the inner ceiling's takgles has to run along
 * z to cross them (issue 115), and why the ceiling insulation bays are the
 * gaps in this list.
 *
 * Single source for the three roof branches and the interior ceiling; the
 * bays used to be derivable only by re-deriving the same three literals.
 */
export function getRoofJoistZPositions(ctx: SquareBaseBuildContext): number[] {
	// Inset half a joist width at each end so the end joists sit inside the
	// wall line, then fill at no more than the standard c/c.
	return roofFramePositions(ctx.L, ctx.fW, CC);
}

/** X stretches of joist `index` (running fromX → toX) left standing by `trim`. */
function joistPieces(
	trim: MemberRowTrim | null,
	index: number,
	fromX: number,
	toX: number,
): [number, number][] {
	return memberPiecesAround(
		fromX,
		toX,
		trim?.cutIndices.includes(index) ? trim.gapAlongM : null,
	);
}

/**
 * The first piece keeps the joist's name; the far side of a cut is
 * `-trimmed` — the same board, cut around the flue, which is how the assembly
 * film's kit count reads it (`countAssemblyKitParts`).
 */
const pieceName = (name: string, piece: number) =>
	piece === 0 ? name : `${name}${TRIMMED_PIECE_SUFFIX}`;

/**
 * The two headers of a joist trim, each a joist-section board along Z between
 * the uncut joists either side. `yAt(x, z)` is the joist centre height there;
 * `shearX` the joists' own shear across X at that x (0 for level joists).
 * A header between level joists stepped along Z (mono, front/back high) is
 * sheared along Z instead, from the two ends' heights.
 */
function addRoofJoistHeaders(
	ctx: SquareBaseBuildContext,
	group: THREE.Group,
	namePrefix: string,
	trim: MemberRowTrim | null,
	yAt: (x: number, z: number) => number,
	shearX: (x: number) => number,
) {
	if (!trim) return;
	trim.headers.forEach((header, index) => {
		const z0 = header.fromAcrossM;
		const z1 = header.toAcrossM;
		const y0 = yAt(header.alongM, z0);
		const y1 = yAt(header.alongM, z1);
		const alongZ = Math.abs(y1 - y0) > 1e-6;
		addSlopedBox(
			ctx.fW,
			ctx.rH,
			z1 - z0,
			matWood,
			header.alongM,
			(y0 + y1) / 2,
			(z0 + z1) / 2,
			group,
			alongZ ? Math.atan2(y1 - y0, z1 - z0) : shearX(header.alongM),
			alongZ ? "z" : "x",
		).name = `detail/roof-joist/${namePrefix}-chimney-header-${index + 1}`;
	});
}

function getRoofCoveringMaterial(ctx: SquareBaseBuildContext) {
	return normalizeRoofSystemId(
		ctx.roofConfig.kind === "barrel" ? undefined : ctx.roofConfig.systemId,
	) === "sheet_metal"
		? matRoofSheetMetal
		: matRoofFelt;
}

export function getRoofCoveringThickness(ctx: SquareBaseBuildContext) {
	return normalizeRoofSystemId(
		ctx.roofConfig.kind === "barrel" ? undefined : ctx.roofConfig.systemId,
	) === "sheet_metal"
		? ROOF_SHEET_T
		: ROOF_FELT_T;
}

export function buildRoof(
	ctx: SquareBaseBuildContext,
	roofGroup: THREE.Group,
	frontRoofGroup: THREE.Group,
	options: RoofBuildOptions,
) {
	const systemId = normalizeRoofSystemId(
		ctx.roofConfig.kind === "barrel" ? undefined : ctx.roofConfig.systemId,
	);
	const roofCoveringMaterial = getRoofCoveringMaterial(ctx);
	const roofCoveringThickness = getRoofCoveringThickness(ctx);
	const roofDeckingThickness = roofDeckingVerticalThicknessMm(options.style === "flat" ? 0 : ctx.pitchRad) / 1000;

	const trim = planRoofJoistTrim(ctx, options.flue);

	if (options.style === "ceiling") {
		const joistY = ctx.wallBase + ctx.H + ctx.rH / 2;
		getRoofJoistZPositions(ctx).forEach((zPos, index) => {
			joistPieces(trim, index, -ctx.W / 2, ctx.W / 2).forEach(([a, b], piece) => {
				addBox(b - a, ctx.rH, ctx.fW, matWood, (a + b) / 2, joistY, zPos, roofGroup).name =
					pieceName(`detail/roof-joist/${options.namePrefix}-${index + 1}`, piece);
			});
		});
		addRoofJoistHeaders(ctx, roofGroup, options.namePrefix, trim, () => joistY, () => 0);
		return;
	}

	if (options.style === "flat") {
		const roofBase = ctx.wallBase + ctx.H;
		const joistY = roofBase + ctx.rH / 2;
		const joistZPositions = getRoofJoistZPositions(ctx);
		const halfSpan = ctx.W / 2 + 0.2;
		joistZPositions.forEach((zPos, index) => {
			joistPieces(trim, index, -halfSpan, halfSpan).forEach(([a, b], piece) => {
				addBox(b - a, ctx.rH, ctx.fW, matWood, (a + b) / 2, joistY, zPos, roofGroup).name =
					pieceName(`detail/roof-joist/${options.namePrefix}-${index + 1}`, piece);
			});
		});
		addRoofJoistHeaders(ctx, roofGroup, options.namePrefix, trim, () => joistY, () => 0);
		addBox(
			ctx.W + 0.4,
			roofDeckingThickness,
			ctx.L + 0.2,
			matRoofTop,
			0,
			roofBase + ctx.rH + roofDeckingThickness / 2,
			0,
			roofGroup,
		).name = `detail/roof-plane/${options.namePrefix}-top`;
		addBox(
			ctx.W + 0.45,
			0.12,
			0.03,
			matFasciaHorizontal,
			0,
			roofBase + ctx.rH + 0.075,
			ctx.L / 2 + 0.115,
			frontRoofGroup,
		).name = `detail/silhouette/${options.namePrefix}-fascia-front`;
		addBox(
			ctx.W + 0.45,
			0.12,
			0.03,
			matFasciaHorizontal,
			0,
			roofBase + ctx.rH + 0.075,
			-ctx.L / 2 - 0.115,
			roofGroup,
		).name = `detail/silhouette/${options.namePrefix}-fascia-back`;
		// Side fasciae stop at the inner Z face of the front/back fasciae
		// (which sit at z = ±(L/2 + 0.115) with thickness 0.03, so their
		// inner faces are at z = ±(L/2 + 0.1)). At the original L+0.23
		// length the side ends overlapped the front/back fascia volume
		// by 15 mm and z-fought visibly under the eave on rotation.
		const sideFasciaLength = ctx.L + 0.2;
		addBox(
			0.03,
			0.12,
			sideFasciaLength,
			matFasciaHorizontal,
			-ctx.W / 2 - 0.215,
			roofBase + ctx.rH + 0.075,
			0,
			roofGroup,
		).name = `detail/silhouette/${options.namePrefix}-fascia-left`;
		addBox(
			0.03,
			0.12,
			sideFasciaLength,
			matFasciaHorizontal,
			ctx.W / 2 + 0.215,
			roofBase + ctx.rH + 0.075,
			0,
			roofGroup,
		).name = `detail/silhouette/${options.namePrefix}-fascia-right`;
		return;
	}

	if (ctx.roofConfig.kind === "gable") {
		const roofOverhang = 0.2;
		const joistZPositions = getRoofJoistZPositions(ctx);
		const halfRun = ctx.W / 2 + roofOverhang;
		const roofPlaneL = ctx.L + 2 * roofOverhang;
		const roofBaseY = ctx.wallBase + ctx.H;
		const wallRise = ctx.roofRise;
		const halfCenterX = halfRun / 2;
		const localXAtWall = halfCenterX - ctx.W / 2;
		const joistCenterY =
			roofBaseY + ctx.rH / 2 - localXAtWall * Math.tan(ctx.pitchRad);
		const joistBottomAtEaveY =
			joistCenterY - ctx.rH / 2 - halfCenterX * Math.tan(ctx.pitchRad);
		const roofDeckBaseY = joistCenterY + ctx.rH / 2;
		const roofUnderlayBaseY = roofDeckBaseY + roofDeckingThickness;
		const roofCoveringBaseY = roofUnderlayBaseY + ROOF_UNDERLAY_T;
		const roofEdgeTopY =
			joistBottomAtEaveY +
			ctx.rH +
			roofDeckingThickness +
			ROOF_UNDERLAY_T +
			roofCoveringThickness;

		// NOTE: `addSlopedBox` SHEARS vertices in Y — it does not rotate the
		// box — so the width passed in IS the horizontal run. The joists and
		// trusses used to pass `halfRun / cos(pitch)` (the along-slope board
		// length), which made every rafter ~W·(1/cos−1)/2 too long in X: the
		// tails poked 45mm through the eave fascia as a row of dark blocks,
		// and the ridge ends crossed the centreline into the opposite slope's
		// rafters. Pass the horizontal run so tails end at the fascia's inner
		// face and ridge ends butt at x=0.
		//
		// The end positions (index 0 and last) are taken by the gable trusses
		// below — the interior loop skips them so the truss meshes aren't
		// duplicated by coincident joists.
		// Rafter centre height at x: both halves fall from the ridge at the
		// pitch, through `joistCenterY` at their own centres (±halfCenterX).
		const rafterYAt = (x: number) =>
			joistCenterY + (halfCenterX - Math.abs(x)) * Math.tan(ctx.pitchRad);
		const rafterShear = (x: number) => -Math.sign(x) * ctx.pitchRad;
		for (let index = 1; index < joistZPositions.length - 1; index += 1) {
			const zPos = joistZPositions[index];
			for (const [side, from, to] of [
				["left", -halfRun, 0],
				["right", 0, halfRun],
			] as const) {
				joistPieces(trim, index, from, to).forEach(([a, b], piece) => {
					const centerX = (a + b) / 2;
					addSlopedBox(
						b - a,
						ctx.rH,
						ctx.fW,
						matWood,
						centerX,
						rafterYAt(centerX),
						zPos,
						roofGroup,
						rafterShear(centerX),
						"x",
					).name = pieceName(
						`detail/roof-joist/${options.namePrefix}-${side}-${index + 1}`,
						piece,
					);
				});
			}
		}
		addRoofJoistHeaders(
			ctx,
			roofGroup,
			options.namePrefix,
			trim,
			(x) => rafterYAt(x),
			rafterShear,
		);

		// Gable trusses sit at the very ends of the roof — but INSIDE the
		// wall line, like every other framing member. They used to be
		// centred exactly ON the wall plane (z = ±L/2), which pushed half
		// the truss (fW/2 ≈ 23mm) outside the framing — 1mm proud of the
		// cladding's outer face — so a wide horizontally-grained framing
		// board covered the gable cladding and z-fought with it.
		const gableTrussZPositions = [
			{ z: -ctx.L / 2 + ctx.fW / 2, label: "back", group: roofGroup },
			{ z: ctx.L / 2 - ctx.fW / 2, label: "front", group: frontRoofGroup },
		];
		for (const { z, label, group: trussGroup } of gableTrussZPositions) {
			addSlopedBox(
				halfRun,
				ctx.rH,
				ctx.fW,
				matWood,
				-halfCenterX,
				joistCenterY,
				z,
				trussGroup,
				ctx.pitchRad,
				"x",
			).name = `detail/roof-truss/${options.namePrefix}-${label}-left`;
			addSlopedBox(
				halfRun,
				ctx.rH,
				ctx.fW,
				matWood,
				halfCenterX,
				joistCenterY,
				z,
				trussGroup,
				-ctx.pitchRad,
				"x",
			).name = `detail/roof-truss/${options.namePrefix}-${label}-right`;
		}

		addSlopedBox(
			halfRun,
			roofDeckingThickness,
			roofPlaneL,
			matRoofTop,
			-halfCenterX,
			roofDeckBaseY + roofDeckingThickness / 2,
			0,
			roofGroup,
			ctx.pitchRad,
			"x",
		).name = `detail/roof-plane/${options.namePrefix}-left`;
		addSlopedBox(
			halfRun,
			roofDeckingThickness,
			roofPlaneL,
			matRoofTop,
			halfCenterX,
			roofDeckBaseY + roofDeckingThickness / 2,
			0,
			roofGroup,
			-ctx.pitchRad,
			"x",
		).name = `detail/roof-plane/${options.namePrefix}-right`;
		addSlopedBox(
			halfRun,
			ROOF_UNDERLAY_T,
			roofPlaneL - 0.04,
			matRoofUnderlay,
			-halfCenterX,
			roofUnderlayBaseY + ROOF_UNDERLAY_T / 2,
			0,
			roofGroup,
			ctx.pitchRad,
			"x",
		).name = `detail/roof-underlay/${options.namePrefix}-left`;
		addSlopedBox(
			halfRun,
			ROOF_UNDERLAY_T,
			roofPlaneL - 0.04,
			matRoofUnderlay,
			halfCenterX,
			roofUnderlayBaseY + ROOF_UNDERLAY_T / 2,
			0,
			roofGroup,
			-ctx.pitchRad,
			"x",
		).name = `detail/roof-underlay/${options.namePrefix}-right`;
		addSlopedBox(
			halfRun,
			roofCoveringThickness,
			roofPlaneL - 0.08,
			roofCoveringMaterial,
			-halfCenterX,
			roofCoveringBaseY + roofCoveringThickness / 2,
			0,
			roofGroup,
			ctx.pitchRad,
			"x",
		).name = `detail/roof-covering/${options.namePrefix}-left`;
		addSlopedBox(
			halfRun,
			roofCoveringThickness,
			roofPlaneL - 0.08,
			roofCoveringMaterial,
			halfCenterX,
			roofCoveringBaseY + roofCoveringThickness / 2,
			0,
			roofGroup,
			-ctx.pitchRad,
			"x",
		).name = `detail/roof-covering/${options.namePrefix}-right`;

		const fasciaH = ctx.rH + 0.03;
		addSlopedBox(
			0.022,
			fasciaH,
			roofPlaneL + 0.04,
			matFasciaHorizontal,
			-ctx.W / 2 - roofOverhang - 0.011,
			joistBottomAtEaveY + fasciaH / 2,
			0,
			roofGroup,
			ctx.pitchRad,
			"x",
		).name = `detail/silhouette/${options.namePrefix}-fascia-left`;
		addSlopedBox(
			0.022,
			fasciaH,
			roofPlaneL + 0.04,
			matFasciaHorizontal,
			ctx.W / 2 + roofOverhang + 0.011,
			joistBottomAtEaveY + fasciaH / 2,
			0,
			roofGroup,
			-ctx.pitchRad,
			"x",
		).name = `detail/silhouette/${options.namePrefix}-fascia-right`;
		addSlopedBox(
			halfRun + 0.022,
			fasciaH,
			0.022,
			matFasciaHorizontal,
			-halfCenterX,
			joistCenterY - ctx.rH / 2 + fasciaH / 2 + 0.01,
			ctx.L / 2 + roofOverhang + 0.011,
			frontRoofGroup,
			ctx.pitchRad,
			"x",
		).name = `detail/silhouette/${options.namePrefix}-fascia-front-left`;
		addSlopedBox(
			halfRun + 0.022,
			fasciaH,
			0.022,
			matFasciaHorizontal,
			halfCenterX,
			joistCenterY - ctx.rH / 2 + fasciaH / 2 + 0.01,
			ctx.L / 2 + roofOverhang + 0.011,
			frontRoofGroup,
			-ctx.pitchRad,
			"x",
		).name = `detail/silhouette/${options.namePrefix}-fascia-front-right`;
		addSlopedBox(
			halfRun + 0.022,
			fasciaH,
			0.022,
			matFasciaHorizontal,
			-halfCenterX,
			joistCenterY - ctx.rH / 2 + fasciaH / 2 + 0.01,
			-ctx.L / 2 - roofOverhang - 0.011,
			roofGroup,
			ctx.pitchRad,
			"x",
		).name = `detail/silhouette/${options.namePrefix}-fascia-back-left`;
		addSlopedBox(
			halfRun + 0.022,
			fasciaH,
			0.022,
			matFasciaHorizontal,
			halfCenterX,
			joistCenterY - ctx.rH / 2 + fasciaH / 2 + 0.01,
			-ctx.L / 2 - roofOverhang - 0.011,
			roofGroup,
			-ctx.pitchRad,
			"x",
		).name = `detail/silhouette/${options.namePrefix}-fascia-back-right`;

		const ridgeSurfaceTopY =
			roofBaseY +
			wallRise +
			ctx.rH +
			roofDeckingThickness +
			ROOF_UNDERLAY_T +
			roofCoveringThickness;
		// Ridge profile rides ON the capping (1mm embed, never flush) — a
		// buried band whose top face is coplanar with the capping z-fights
		// along the whole ridge. Both parts inherit the two roof slopes; the
		// previous flat boxes floated ~32mm above the covering at their edges.
		const ridgeCappingGap = 0.002;
		addBentGableRidgeStrip({
			totalWidth: 0.12,
			height: ROOF_FLASHING_T,
			length: roofPlaneL - 0.08,
			material: matRoofFlashing,
			ridgeContactY: ridgeSurfaceTopY + ridgeCappingGap,
			pitchRad: ctx.pitchRad,
			group: roofGroup,
			name: `detail/roof-flashing/${options.namePrefix}-ridge-capping`,
		});
		addBentGableRidgeStrip({
			totalWidth: 0.03,
			height: 0.04,
			length: roofPlaneL - 0.08,
			material: matBand,
			ridgeContactY:
				ridgeSurfaceTopY + ridgeCappingGap + ROOF_FLASHING_T - 0.001,
			pitchRad: ctx.pitchRad,
			group: roofGroup,
			name: `detail/silhouette/${options.namePrefix}-roof-ridge`,
		});
		addBox(
			0.12,
			ROOF_FLASHING_T,
			roofPlaneL - 0.12,
			matRoofFlashing,
			-ctx.W / 2 - roofOverhang + 0.06,
			roofEdgeTopY + ROOF_FLASHING_T / 2 + 0.002,
			0,
			roofGroup,
		).name = `detail/roof-flashing/${options.namePrefix}-eave-left`;
		addBox(
			0.12,
			ROOF_FLASHING_T,
			roofPlaneL - 0.12,
			matRoofFlashing,
			ctx.W / 2 + roofOverhang - 0.06,
			roofEdgeTopY + ROOF_FLASHING_T / 2 + 0.002,
			0,
			roofGroup,
		).name = `detail/roof-flashing/${options.namePrefix}-eave-right`;

		if (systemId === "sheet_metal") {
			const seamInset = 0.18;
			const seamCount = Math.max(3, Math.round(ctx.L / 0.8));
			const seamSpan = Math.max(0.5, roofPlaneL - seamInset * 2);
			for (let index = 0; index < seamCount; index += 1) {
				const zPos =
					-roofPlaneL / 2 +
					seamInset +
					(seamSpan * index) / Math.max(1, seamCount - 1);
				addSlopedBox(
					halfRun - 0.12,
					roofCoveringThickness + 0.006,
					0.014,
					matBand,
					-halfCenterX + 0.03,
					roofCoveringBaseY + roofCoveringThickness / 2 + 0.004,
					zPos,
					roofGroup,
					ctx.pitchRad,
					"x",
				).name =
					`detail/silhouette/${options.namePrefix}-roof-seam-left-${index + 1}`;
				addSlopedBox(
					halfRun - 0.12,
					roofCoveringThickness + 0.006,
					0.014,
					matBand,
					halfCenterX - 0.03,
					roofCoveringBaseY + roofCoveringThickness / 2 + 0.004,
					zPos,
					roofGroup,
					-ctx.pitchRad,
					"x",
				).name =
					`detail/silhouette/${options.namePrefix}-roof-seam-right-${index + 1}`;
			}
		}

		// Open eaves: exposed rafter tails. A solid wedge here concealed the
    // structural tails and falsely represented an entire boxed soffit.

		// No separate gable-fill triangles: the wall cladding strips follow
		// the gable profile all the way to the ridge (splitRangeAtProfileBreaks
		// in wallStrips.ts), so an extruded triangle here is redundant — and
		// in the cladding-only facade mode it occupied the exact same slab as
		// the cladding strips and shimmered against them.

		const gableInfillCC = CC;
		const numGableInfillStuds = Math.max(
			0,
			Math.ceil((ctx.W - ctx.sW) / gableInfillCC) - 1,
		);
		const gableInfillSpacing = ctx.W / (numGableInfillStuds + 1);
		for (const sz of [-1, 1]) {
			const zPos = sz === 1 ? ctx.L / 2 - ctx.sD / 2 : -ctx.L / 2 + ctx.sD / 2;
			const targetGroup = sz === 1 ? frontRoofGroup : roofGroup;
			for (let index = 1; index <= numGableInfillStuds; index += 1) {
				const xPos = -ctx.W / 2 + index * gableInfillSpacing;
				const distFromCenter = Math.abs(xPos);
				const infillHeight =
					(ctx.W / 2 - distFromCenter) * Math.tan(ctx.pitchRad);
				if (infillHeight < ctx.sW * 2) continue;
				const antiZInfill = 0.002;
				addBox(
					ctx.sW,
					infillHeight - antiZInfill,
					ctx.sD,
					matWood,
					xPos,
					roofBaseY + antiZInfill + (infillHeight - antiZInfill) / 2,
					zPos,
					targetGroup,
				).name =
					`detail/framing/gable-infill-${sz === 1 ? "front" : "back"}-${index}`;
			}
		}

		return;
	}

	buildMonoRoof(ctx, roofGroup, frontRoofGroup, options, {
		systemId,
		roofCoveringMaterial,
		roofCoveringThickness,
		roofDeckingThickness,
	});
}

type RectWall = "front" | "back" | "left" | "right";

/**
 * The mono roof written once in a CANONICAL frame — `r` runs from the low
 * wall line (r = -run/2) to the high wall line (r = +run/2), `e` along the
 * eaves — and mapped onto the building by the roof's `monoSlope`. The
 * historical front-high roof is the identity mapping (e → x, r → z), so its
 * meshes are unchanged; a left/right high wall maps r onto ±x.
 *
 * `addSlopedBox` shears along the WORLD axis, so a canonical box sheared by
 * `pitch` along +r becomes one sheared by `sign·pitch` along the run axis.
 */
function buildMonoRoof(
	ctx: SquareBaseBuildContext,
	roofGroup: THREE.Group,
	frontRoofGroup: THREE.Group,
	options: RoofBuildOptions,
	covering: {
		systemId: ReturnType<typeof normalizeRoofSystemId>;
		roofCoveringMaterial: THREE.Material;
		roofCoveringThickness: number;
		roofDeckingThickness: number;
	},
) {
	const {
		systemId,
		roofCoveringMaterial,
		roofCoveringThickness,
		roofDeckingThickness,
	} = covering;
	const slope = ctx.roofGeometry.monoSlope;
	const axis = slope?.axis ?? "z";
	const sign = slope?.sign ?? 1;
	const runLength = slope?.runLength ?? ctx.L;
	const eaveLength = slope?.eaveLength ?? ctx.W;
	const tanPitch = Math.tan(ctx.pitchRad);
	const shear = sign * ctx.pitchRad;
	const alongZ = axis === "z";

	/** World wall for a canonical side (high = +r, low = -r, ±e the rakes). */
	const worldWall = (side: "high" | "low" | "eMinus" | "ePlus"): RectWall => {
		if (side === "high") return slope?.highWall ?? "front";
		if (side === "low") return slope?.lowWall ?? "back";
		if (alongZ) return side === "eMinus" ? "left" : "right";
		return side === "eMinus" ? "back" : "front";
	};
	const groupFor = (wall: RectWall) =>
		wall === "front" ? frontRoofGroup : roofGroup;
	const place = (e: number, r: number) =>
		alongZ ? { x: e, z: sign * r } : { x: sign * r, z: e };
	const size = (dimE: number, dimR: number) =>
		alongZ ? { w: dimE, d: dimR } : { w: dimR, d: dimE };
	const slopedBox = (
		dimE: number,
		h: number,
		dimR: number,
		material: THREE.Material,
		e: number,
		y: number,
		r: number,
		group: THREE.Group,
	) => {
		const { w, d } = size(dimE, dimR);
		const { x, z } = place(e, r);
		return addSlopedBox(w, h, d, material, x, y, z, group, shear, axis);
	};
	const levelBox = (
		dimE: number,
		h: number,
		dimR: number,
		material: THREE.Material,
		e: number,
		y: number,
		r: number,
		group: THREE.Group,
	) => {
		const { w, d } = size(dimE, dimR);
		const { x, z } = place(e, r);
		return addBox(w, h, d, material, x, y, z, group);
	};

	const roofOverhang = 0.2;
	const monoJoistZPositions = getRoofJoistZPositions(ctx);
	const trim = planRoofJoistTrim(ctx, options.flue);
	const halfSpan = ctx.W / 2 + roofOverhang;
	if (alongZ) {
		// Front/back high wall: level joists spanning the width, stepped up
		// the fall — they bear on the raked side walls.
		const joistYAt = (zPos: number) =>
			(sign === 1
				? ctx.roofBaseBack + ((zPos - -ctx.L / 2) / ctx.L) * ctx.roofRise
				: ctx.wallBase + ctx.roofGeometry.getRoofHeightAt(0, zPos)) +
			ctx.rH / 2;
		monoJoistZPositions.forEach((zPos, index) => {
			joistPieces(trim, index, -halfSpan, halfSpan).forEach(([a, b], piece) => {
				addBox(
					b - a,
					ctx.rH,
					ctx.fW,
					matWood,
					(a + b) / 2,
					joistYAt(zPos),
					zPos,
					roofGroup,
				).name = pieceName(
					`detail/roof-joist/${options.namePrefix}-${index + 1}`,
					piece,
				);
			});
		});
		addRoofJoistHeaders(
			ctx,
			roofGroup,
			options.namePrefix,
			trim,
			(_x, z) => joistYAt(z),
			() => 0,
		);
	} else {
		// Left/right high wall: the joists are rafters spanning the width
		// from the low eave wall to the high one (bearing on the two long
		// walls), sloped with the roof and spread along the length at the
		// same Z centres the ceiling bays read.
		const rafterYAt = (x: number) =>
			ctx.roofMidY + ctx.rH / 2 + x * Math.tan(shear);
		monoJoistZPositions.forEach((zPos, index) => {
			joistPieces(trim, index, -halfSpan, halfSpan).forEach(([a, b], piece) => {
				const centerX = (a + b) / 2;
				addSlopedBox(
					b - a,
					ctx.rH,
					ctx.fW,
					matWood,
					centerX,
					rafterYAt(centerX),
					zPos,
					roofGroup,
					shear,
					"x",
				).name = pieceName(
					`detail/roof-joist/${options.namePrefix}-${index + 1}`,
					piece,
				);
			});
		});
		addRoofJoistHeaders(
			ctx,
			roofGroup,
			options.namePrefix,
			trim,
			(x) => rafterYAt(x),
			() => shear,
		);
	}

	const roofPlaneW = eaveLength + 2 * roofOverhang;
	const roofPlaneL = runLength + 2 * roofOverhang;
	const roofDeckBaseY = ctx.roofMidY + ctx.rH;
	const roofUnderlayBaseY = roofDeckBaseY + roofDeckingThickness;
	const roofCoveringBaseY = roofUnderlayBaseY + ROOF_UNDERLAY_T;
	slopedBox(
		roofPlaneW,
		roofDeckingThickness,
		roofPlaneL,
		matRoofTop,
		0,
		roofDeckBaseY + roofDeckingThickness / 2,
		0,
		roofGroup,
	).name = `detail/roof-plane/${options.namePrefix}-top`;
	slopedBox(
		roofPlaneW - 0.04,
		ROOF_UNDERLAY_T,
		roofPlaneL - 0.04,
		matRoofUnderlay,
		0,
		roofUnderlayBaseY + ROOF_UNDERLAY_T / 2,
		0,
		roofGroup,
	).name = `detail/roof-underlay/${options.namePrefix}-top`;
	slopedBox(
		roofPlaneW - 0.08,
		roofCoveringThickness,
		roofPlaneL - 0.08,
		roofCoveringMaterial,
		0,
		roofCoveringBaseY + roofCoveringThickness / 2,
		0,
		roofGroup,
	).name = `detail/roof-covering/${options.namePrefix}-top`;

	const soffitMaterial = matFascia.clone();
	soffitMaterial.side = THREE.DoubleSide;
	let soffit: THREE.Mesh;
	if (alongZ && sign === 1) {
		soffit = new THREE.Mesh(
			new THREE.PlaneGeometry(roofPlaneW, roofPlaneL),
			soffitMaterial,
		);
		soffit.rotation.x = -Math.PI / 2 - ctx.pitchRad;
	} else {
		// Horizontal plane (run along its world axis), sheared up the fall
		// exactly like the boxes above it.
		const { w, d } = size(roofPlaneW, roofPlaneL);
		const geometry = new THREE.PlaneGeometry(w, d);
		geometry.rotateX(-Math.PI / 2);
		const positions = geometry.attributes.position;
		for (let index = 0; index < positions.count; index += 1) {
			const run = axis === "x" ? positions.getX(index) : positions.getZ(index);
			positions.setY(index, run * Math.tan(shear));
		}
		geometry.computeVertexNormals();
		soffit = new THREE.Mesh(geometry, soffitMaterial);
	}
	soffit.position.set(0, ctx.roofMidY + ctx.rH - 0.018, 0);
	soffit.castShadow = true;
	soffit.receiveShadow = true;
	soffit.name = `detail/eave/${options.namePrefix}-soffit-panel`;
	roofGroup.add(soffit);

	const fasciaH = ctx.rH + 0.03;
	const fasciaFrontW = roofPlaneW + 0.044;
	const highFasciaR = runLength / 2 + roofOverhang + 0.011;
	const lowFasciaR = -runLength / 2 - roofOverhang - 0.011;
	// Along Z the joists are level and stop inside the eave walls, so the
	// eave fascia just follows the roof plane. Across the width the joists are
	// RAFTERS whose tails end at the fascia: anchor its bottom to the rafter
	// underside at the tail tip (the gable rule, 2026-04-07), or the tails peek
	// out ~1 cm below it along the whole eave.
	const rafterTipR = runLength / 2 + roofOverhang;
	const highFasciaY = alongZ
		? ctx.roofMidY + fasciaH / 2 + 0.01 + highFasciaR * tanPitch
		: ctx.roofMidY + rafterTipR * tanPitch + fasciaH / 2;
	const lowFasciaY = alongZ
		? ctx.roofMidY + fasciaH / 2 + 0.01 + lowFasciaR * tanPitch
		: ctx.roofMidY - rafterTipR * tanPitch + fasciaH / 2;
	// Drip edge sits at the LOW overhang tip, ~140 mm beyond the low wall
	// line. On a mono pitch the covering surface there is already
	// `eaveLowR * tan(pitch)` lower than at the wall, so anchor the flashing
	// to the covering top at its own position — not at the wall-line height —
	// or it floats above the eave. (Same endpoint-anchoring rule as the gable
	// fascia; see 2026-04-07-gable-fascia-must-anchor-to-eave-line.md.)
	const eaveLowR = -runLength / 2 - roofOverhang + 0.06;
	const eaveLowCoveringTopY =
		roofCoveringBaseY + roofCoveringThickness + eaveLowR * tanPitch;

	const highWall = worldWall("high");
	const lowWall = worldWall("low");
	levelBox(
		fasciaFrontW,
		fasciaH,
		0.022,
		matFasciaHorizontal,
		0,
		highFasciaY,
		highFasciaR,
		groupFor(highWall),
	).name = `detail/silhouette/${options.namePrefix}-fascia-${highWall}`;
	levelBox(
		fasciaFrontW,
		fasciaH,
		0.022,
		matFasciaHorizontal,
		0,
		lowFasciaY,
		lowFasciaR,
		groupFor(lowWall),
	).name = `detail/silhouette/${options.namePrefix}-fascia-${lowWall}`;
	for (const [side, e] of [
		["eMinus", -eaveLength / 2 - roofOverhang],
		["ePlus", eaveLength / 2 + roofOverhang],
	] as const) {
		const wall = worldWall(side);
		slopedBox(
			0.022,
			fasciaH,
			roofPlaneL,
			matFasciaHorizontal,
			e,
			ctx.roofMidY + fasciaH / 2 + 0.01,
			0,
			groupFor(wall),
		).name = `detail/silhouette/${options.namePrefix}-fascia-${wall}`;
	}
	slopedBox(
		roofPlaneW - 0.12,
		ROOF_FLASHING_T,
		0.12,
		matRoofFlashing,
		0,
		eaveLowCoveringTopY + ROOF_FLASHING_T / 2 + 0.002,
		eaveLowR,
		groupFor(lowWall),
	).name = `detail/roof-flashing/${options.namePrefix}-eave-${lowWall}`;

	const eaveCloserDepth = roofOverhang + 0.01;
	const eaveCloserH = ctx.rH;
	// Stop at the inner face of the two eave walls' studs, not at the outer
	// wall plane — otherwise the closer's ends sit on top of that framing and
	// z-fight in the corner under the eave (same fix already applied to the
	// gable path; see
	// 2026-04-11-gable-eave-closers-must-stop-at-truss-inner-faces.md).
	const eaveCloserSpan = Math.max(0.01, runLength - 2 * ctx.sD);
	for (const sx of [-1, 1]) {
		const wall = worldWall(sx === -1 ? "eMinus" : "ePlus");
		slopedBox(
			eaveCloserDepth,
			eaveCloserH,
			eaveCloserSpan,
			matExteriorCladding,
			sx * (eaveLength / 2 + eaveCloserDepth / 2),
			ctx.roofMidY + eaveCloserH / 2,
			0,
			groupFor(wall),
		).name = `detail/eave/${options.namePrefix}-closer-${wall}`;
	}

	if (systemId === "sheet_metal") {
		const seamCount = Math.max(3, Math.round(eaveLength / 0.7));
		const seamInset = 0.22;
		const seamSpan = Math.max(0.4, roofPlaneW - seamInset * 2);
		for (let index = 0; index < seamCount; index += 1) {
			slopedBox(
				0.018,
				roofCoveringThickness + 0.01,
				roofPlaneL - 0.08,
				matBand,
				-roofPlaneW / 2 +
					seamInset +
					(seamSpan * index) / Math.max(1, seamCount - 1),
				roofCoveringBaseY + roofCoveringThickness / 2 + 0.006,
				0,
				roofGroup,
			).name = `detail/silhouette/${options.namePrefix}-roof-seam-${index + 1}`;
		}
	}
}
